import { useEffect, useMemo, useState } from 'react';
import { Loader2, LayoutGrid, Users, Layers, Search, X } from 'lucide-react';
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent,
  PointerSensor, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { ticketsApi, type TicketBrief, type TicketStatus } from '../api/tickets';
import { authApi } from '../api/auth';
import { fmtTime, ticketNumber, PRIORITY_COLOR, STATUS_LABEL } from '../lib/format';
import { useAuth, type AuthUser } from '../lib/auth';

type ViewMode = 'status' | 'assignee' | 'line';

const STATUS_COLUMNS: Array<{ id: TicketStatus; label: string }> = [
  { id: 'new',               label: STATUS_LABEL.new },
  { id: 'open',              label: STATUS_LABEL.open },
  { id: 'pending_customer',  label: STATUS_LABEL.pending_customer },
  { id: 'resolved',          label: STATUS_LABEL.resolved },
  { id: 'closed',            label: STATUS_LABEL.closed },
];

const LINE_COLUMNS: Array<{ id: string; label: string }> = [
  { id: 'unassigned', label: 'Без линии' },
  { id: 'L1',         label: '1-я линия' },
  { id: 'L2',         label: '2-я линия' },
  { id: 'L3',         label: '3-я линия' },
];

const PRIORITY_BAR: Record<string, string> = {
  P1: 'bg-red-500',
  P2: 'bg-orange-500',
  P3: 'bg-blue-500',
  P4: 'bg-slate-500',
};

export default function BoardPage() {
  const { user } = useAuth();
  const isStaff = user?.type === 'staff';
  const [items, setItems] = useState<TicketBrief[]>([]);
  const [staff, setStaff] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(() =>
    (localStorage.getItem('board.view') as ViewMode) || 'status'
  );
  const [search, setSearch] = useState('');

  const switchView = (v: ViewMode) => { setView(v); localStorage.setItem('board.view', v); };

  // Локальная фильтрация по subject / org / requester / SD-NNNN
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    const digits = q.replace(/\D+/g, '');
    return items.filter(t => {
      if (digits && String(t.number).padStart(4,'0').includes(digits)) return true;
      if (digits && String(t.number).includes(digits)) return true;
      if (t.subject?.toLowerCase().includes(q)) return true;
      if (t.organization_name?.toLowerCase().includes(q)) return true;
      if (t.requester_name?.toLowerCase().includes(q)) return true;
      if (t.requester_email?.toLowerCase().includes(q)) return true;
      if (t.assignee_name?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [items, search]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = async () => {
    setLoading(true);
    try {
      const r = await ticketsApi.list({ limit: 500 });
      setItems(r.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isStaff) authApi.staff().then(setStaff).catch(() => {});
  }, [isStaff]);

  // Колонки текущего режима — строятся из отфильтрованного набора
  const columns = useMemo<Array<{ id: string; label: string; count: number; tickets: TicketBrief[] }>>(() => {
    if (view === 'status') {
      return STATUS_COLUMNS.map(c => {
        const tickets = filteredItems.filter(t => t.status === c.id);
        return { id: c.id, label: c.label, count: tickets.length, tickets };
      });
    }
    if (view === 'line') {
      return LINE_COLUMNS.map(c => {
        const tickets = filteredItems.filter(t => {
          if (c.id === 'unassigned') return t.support_line == null;
          return `L${t.support_line}` === c.id;
        });
        return { id: c.id, label: c.label, count: tickets.length, tickets };
      });
    }
    // assignee mode
    const cols: Array<{ id: string; label: string; count: number; tickets: TicketBrief[] }> = [
      { id: 'unassigned', label: 'Без агента', count: 0, tickets: [] },
    ];
    for (const s of staff) cols.push({ id: s.id, label: s.full_name || s.email, count: 0, tickets: [] });
    for (const t of filteredItems) {
      const key = t.assignee_id || 'unassigned';
      const col = cols.find(c => c.id === key);
      if (col) { col.tickets.push(t); col.count = col.tickets.length; }
    }
    return cols;
  }, [filteredItems, staff, view]);

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    if (!isStaff || !e.over) return;
    const ticketId = String(e.active.id);
    const colId = String(e.over.id);
    const t = items.find(x => x.id === ticketId);
    if (!t) return;
    try {
      if (view === 'status') {
        if (t.status === colId) return;
        setItems(prev => prev.map(x => x.id === ticketId ? { ...x, status: colId as TicketStatus } : x));
        await ticketsApi.update(ticketId, { status: colId as TicketStatus });
      } else if (view === 'line') {
        const newLine: number | null = colId === 'unassigned' ? null : Number(colId.slice(1));
        if (t.support_line === newLine) return;
        setItems(prev => prev.map(x => x.id === ticketId ? { ...x, support_line: newLine } : x));
        await ticketsApi.update(ticketId, { support_line: newLine });
      } else {
        const newAssignee: string | null = colId === 'unassigned' ? null : colId;
        if (t.assignee_id === newAssignee) return;
        const a = staff.find(s => s.id === newAssignee);
        setItems(prev => prev.map(x => x.id === ticketId ? {
          ...x, assignee_id: newAssignee,
          assignee_name: a?.full_name || a?.email || null,
          assignee_email: a?.email || null,
        } : x));
        await ticketsApi.assign(ticketId, { assignee_id: newAssignee || undefined, self_assign: false });
      }
    } catch {
      load();
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-400" /></div>;
  }

  const active = activeId ? items.find(x => x.id === activeId) : null;
  const VIEWS: Array<{ id: ViewMode; label: string; icon: any }> = [
    { id: 'status',   label: 'По статусу',   icon: LayoutGrid },
    { id: 'assignee', label: 'По агентам',   icon: Users },
    { id: 'line',     label: 'По линиям',    icon: Layers },
  ];

  return (
    <div className="space-y-3">
      {/* View switcher + search */}
      <div className="flex flex-wrap gap-2 items-center">
        {VIEWS.map(v => {
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => switchView(v.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase transition ${
                view === v.id ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}>
              <Icon size={13} /> {v.label}
            </button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Тема, клиент, автор, SD-NNNN…"
            className="bg-slate-950 border border-slate-700 rounded-full py-1 pl-9 pr-7 text-xs text-white focus:border-blue-500 outline-none w-72" />
          {search && (
            <button onClick={() => setSearch('')} title="Очистить"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
              <X size={13} />
            </button>
          )}
        </div>
        {search && (
          <span className="text-[11px] text-slate-500">
            {filteredItems.length} из {items.length}
          </span>
        )}
      </div>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="grid gap-4 min-h-[60vh]"
             style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(220px, 1fr))` }}>
          {columns.map(col => (
            <Column key={col.id} id={col.id} label={col.label} count={col.count}>
              {col.tickets.map(t => (
                <DraggableCard key={t.id} t={t} disabled={!isStaff} />
              ))}
            </Column>
          ))}
        </div>
        <DragOverlay>
          {active ? <Card t={active} dragging /> : null}
        </DragOverlay>
      </DndContext>

      {!isStaff && (
        <p className="text-xs text-slate-500 text-center">
          Drag-and-drop доступен только сотрудникам.
        </p>
      )}
    </div>
  );
}

function Column({ id, label, count, children }:
  { id: string; label: string; count: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef}
      className={`flex flex-col bg-slate-900/40 border rounded-xl p-3 transition ${
        isOver ? 'border-blue-500 bg-slate-900/80' : 'border-slate-800'
      }`}>
      <header className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 truncate">{label}</h3>
        <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full">{count}</span>
      </header>
      <div className="flex flex-col gap-2 min-h-[120px]">
        {children}
      </div>
    </div>
  );
}

function DraggableCard({ t, disabled }: { t: TicketBrief; disabled?: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: t.id, disabled });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      className={`${isDragging ? 'opacity-30' : ''}`}>
      <Card t={t} />
    </div>
  );
}

function Card({ t, dragging }: { t: TicketBrief; dragging?: boolean }) {
  return (
    <div className={`bg-slate-900 border ${dragging ? 'border-blue-500 shadow-2xl' : 'border-slate-800 hover:border-slate-600'}
        rounded-lg overflow-hidden transition relative cursor-grab active:cursor-grabbing`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${PRIORITY_BAR[t.priority]}`} />
      <a href={`/tickets/${t.id}`} onClick={e => { if (dragging) e.preventDefault(); }}
        className="block p-3 pl-4">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-[10px] text-blue-300">{ticketNumber(t.number)}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIORITY_COLOR[t.priority]}`}>
            {t.priority}
          </span>
        </div>
        <h4 className={`text-sm font-medium text-white line-clamp-2 mb-1 ${t.has_new_client_message ? 'font-bold' : ''}`}>
          {t.has_new_client_message && (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 mr-1.5 animate-pulse" />
          )}
          {t.subject}
        </h4>
        {t.organization_name && (
          <div className="text-[10px] truncate flex items-center gap-1 mb-0.5">
            {t.contract_active === false && <span title="Договор не активен" className="text-red-400">●</span>}
            {t.contract_status === 'expiring_soon' && <span title="Истекает" className="text-yellow-400">●</span>}
            <span className={`${t.contract_active === false ? 'text-red-300' : 'text-blue-300'} font-medium`}>{t.organization_name}</span>
          </div>
        )}
        <div className="text-[10px] text-slate-500 truncate flex items-center gap-1.5">
          <span className="truncate">{t.requester_name || t.requester_email || 'Гость'} · {fmtTime(t.created_at)}</span>
          {t.support_line && (
            <span className="ml-auto px-1 rounded bg-slate-800 text-slate-400 shrink-0">L{t.support_line}</span>
          )}
        </div>
      </a>
    </div>
  );
}
